import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, UrlSegment, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { NodeService } from '../../services/node-service/node-service.service';
import { RootcategoriesService } from '../rootcategories/rootcategories.service';
import { CategoriesItem } from '../categories/categories.model';
import { AddCategoryService } from './add-category.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { switchMap, tap } from 'rxjs/operators';

const IS_PARENT_CATEGORY = 'isParentCategory';
const VIEW_TITLE = 'viewTitle';
const PARENT_ID_VALUE = 'parentIdValue';
const MIN_LENGTH_NAME = 3;
const NAME = /^[a-zA-Z0-9 ]*$/;
const SNACKBAR_DURATION = 4000;

@Component({
  selector: 'app-add-category',
  templateUrl: './add-category.component.html',
  styleUrls: ['./add-category.component.css'],
  standalone: false,
})
export class AddCategoryComponent implements OnInit, OnDestroy {
  @Input(IS_PARENT_CATEGORY) isParentCategory: boolean = true;
  @Input(VIEW_TITLE) viewTitle: string = 'Agregar categoría';
  @Input(PARENT_ID_VALUE) parentIdValue: number = 0;

  red: boolean = true;
  loadCsv: boolean = true;
  showForm: boolean = true;
  createCategoryForm!: FormGroup;
  subscription!: Subscription;
  resultUrl!: UrlSegment[];
  categoryToEdit!: CategoriesItem;
  isEditable: boolean = false;
  defaulValues: CategoriesItem = {
    id: 0,
    nombre: '',
    red: false,
  };
  private iconFormData: FormData = new FormData();
  private markerFormData: FormData = new FormData();

  constructor(
    private formBuilder: FormBuilder,
    public route: ActivatedRoute,
    private nodeService: NodeService,
    private rootcategoriesService: RootcategoriesService,
    private addCategoryService: AddCategoryService,
    public router: Router,
    private snackbar: MatSnackBar
  ) {}

  /**
   * If the URL contains the word "edit", then prepare the edition form. Otherwise, prepare the storage
   * form
   */
  ngOnInit(): void {
    this.subscription = this.route.url.subscribe((url) => {
      this.resultUrl = url.filter((item) => item.path === 'edit');

      if (this.resultUrl.length > 0) {
        this.prepareEdition();
      } else {
        this.prepareStorage();
      }
      this.red = this.createCategoryForm.value?.red;
    });
  }

  /**
   * It creates a form group with the default values.
   * @returns The form group that is created by the loadForm() method.
   */
  prepareStorage(): FormGroup {
    this.defaulValues.id = this.parentIdValue;
    this.createCategoryForm = this.loadForm(this.defaulValues);
    return this.createCategoryForm;
  }

  /**
   * * If the category to edit is the root category, then the form is not shown.
   * * If the category to edit is not the root category, then the form is shown.
   * * If the category to edit is not the root category, then the form is loaded with the category to
   * edit.
   * * If the category to edit is the root category, then the form is loaded with the category to edit
   * @returns The form group.
   */
  prepareEdition(): FormGroup {
    this.loadCsv = false;

    this.categoryToEdit = this.nodeService.getCategoryToEdit();

    this.isEditable = this.categoryToEdit?.id === 0 ? false : true;

    if (!this.isEditable) {
      this.showForm = false;
      this.rootcategoriesService.openSnacBar(
        'Para editar una categoría, debe seleccionarla desde su vista.'
      );
    }

    this.createCategoryForm = this.loadForm(this.categoryToEdit);
    return this.createCategoryForm;
  }

  ngOnDestroy(): void {
    this.createCategoryForm?.reset();
    this.subscription?.unsubscribe();
  }

  /**
   * It creates a form group with the data from the categoriesItem.
   * @param {CategoriesItem} data - CategoriesItem
   * @returns The form group.
   */
  loadForm(data: CategoriesItem) {
    return this.formBuilder.group({
      parentId: [
        { value: data.id, disabled: true },
        [Validators.required, Validators.minLength(1)],
      ],
      nombre: [
        data?.nombre,
        [Validators.required, Validators.minLength(MIN_LENGTH_NAME), Validators.pattern(NAME)],
      ],
      fuente: [data?.fuente, Validators.required],
      red: [data?.red],
      description: [data?.descripcion, Validators.required],
    });
  }

  /**
   * * If the category is editable, then we edit the category and upload the icon and marker.
   * * If the category is not editable, then we upload the icon and marker
   * @returns The response from the server.
   */
  save(): void {
    if (this.isEditable) {
      let response: any;

      this.addCategoryService
        .editCategory(this.formToCategorie())
        .pipe(
          tap((r1) => (response = r1)),
          switchMap((r2) => {
            if (this.iconFormData.has('file')) {
              return this.addCategoryService.uploadIcon(this.categoryToEdit.id, this.iconFormData);
            }

            return of(null);
          }),
          switchMap((r3) => {
            if (this.markerFormData.has('file')) {
              return this.addCategoryService.uploadMarker(
                this.categoryToEdit.id,
                this.markerFormData
              );
            }

            return of(null);
          })
        )
        .subscribe((resp) => {
          this.handleRedirect(response);
        });
    } else {
      this.addIconAndMarker().subscribe((resp: any) => {
        this.handleRedirect(resp);
      });
    }
  }

  /**
   * It adds a new category to the database.
   * @returns The response from the server.
   */
  addIconAndMarker(): any {
    let response: any;
    return this.addCategoryService.addCategory(this.formToCategorie()).pipe(
      tap((r1) => (response = r1)),
      switchMap((r2) => {
        if (this.iconFormData.has('file')) {
          return this.addCategoryService.uploadIcon(response.body?.id, this.iconFormData);
        }
        return of(response);
      }),
      switchMap((r3) => {
        if (this.markerFormData.has('file')) {
          return this.addCategoryService.uploadMarker(response.body?.id, this.markerFormData);
        }
        return of(response);
      })
    );
  }

  /**
   * * Create a category object from the form values
   * @returns The category object that will be sent to the API.
   */
  formToCategorie(): any {
    let category = {
      nombre: this.createCategoryForm.get('nombre')?.value,
      fuente: this.createCategoryForm.get('fuente')?.value,
      red: this.red,
      descripcion: this.createCategoryForm.get('description')?.value,
    };

    if (this.isEditable) {
      let editable = {
        id: this.categoryToEdit.id,
        parentId: this.categoryToEdit.parentId,
        lft: this.categoryToEdit.lft,
        rgt: this.categoryToEdit.rgt,
        depth: this.categoryToEdit.depth,
        red: this.red,
        poisCount: this.categoryToEdit.poisCount,
        iconFileName: this.categoryToEdit.iconFileName,
        iconContentType: this.categoryToEdit.iconContentType,
        iconFileSize: this.categoryToEdit.iconFileSize,
        markerFileName: this.categoryToEdit.markerFileName,
        markerContentType: this.categoryToEdit.markerContentType,
        markerFileSize: this.categoryToEdit.markerFileSize,
      };

      Object.assign(category, editable);
    } else {
      let creable = {
        depth: 0,
        parentId: this.parentIdValue ? this.parentIdValue : null,
      };

      Object.assign(category, creable);
    }

    return category;
  }

  /**
   * * If the response status is 200 or 201, then the category was created successfully.
   * * If the response status is not 200 or 201, then the category was not created successfully.
   * * If the response status is 200 or 201, then the category was created successfully.
   * * If the response status is not 200 or 201, then the category was not created successfully.
   * * If the response status is 200 or 201, then the category was created successfully.
   * * If the response status is not 200 or 201, then the category was not created successfully.
   * * If the response status is 200 or 201, then the category was created successfully.
   * * If the response status is not 200 or 201, then the category was not created successfully.
   * * If the response status is 200 or 201, then the category was created successfully.
   * * If the response status is not 200 or 201, then the category was not created successfully.
   * * If the response status is 200
   * @param {any} response - The response from the server.
   * @returns The response is being returned to the handleRedirect function.
   */
  handleRedirect(response: any) {
    if (response.status === 200 || 201) {
      this.openSnackBar('La nueva categoría fue creada con éxito');

      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };
      this.router.onSameUrlNavigation = 'reload';

      if (response.body != null && response.body.parentId != null) {
        this.router.navigate(['/', 'category', response.body.parentId]);
      } else {
        this.router.navigate(['categories']);
      }
    } else if (response.status === 400) {
      this.openSnackBar(response.body);
    } else {
      this.openSnackBar('La nueva categoría no pudo ser creada');
    }
  }

  openSnackBar(message: string): void {
    this.snackbar.open(`${message}`, 'X', {
      duration: SNACKBAR_DURATION,
    });
  }

  setRed(event: any) {
    this.createCategoryForm.value.red = event.checked;
    this.red = event.checked;
  }

  /**
   * *This function is called when the user clicks the "Upload Icon" button.*
   *
   * The function is pretty simple. It takes the file that the user has selected and adds it to the form
   * data object
   * @param {any} evt - any
   */
  onChangeIcon(evt: any) {
    const target: DataTransfer = <DataTransfer>evt.target;
    const file: File = target.files[0];
    this.iconFormData = new FormData();
    this.iconFormData.append('file', file, file.name);
  }

  /**
   * It takes the file from the file input and adds it to the form data.
   * @param {any} evt - any
   */
  onChangeMarker(evt: any) {
    const target: DataTransfer = <DataTransfer>evt.target;
    const file: File = target.files[0];
    this.markerFormData = new FormData();
    this.markerFormData.append('file', file, file.name);
  }
}
